/**
 * Public Trophy Room Redux Slice
 *
 * Manages state for viewing public Trophy Rooms (viewer perspective).
 * No authentication required - public endpoint.
 * Uses gateway injection pattern for testability.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState, StoreExtraDependencies } from '../index';
import type {
  PublicTrophyRoomData,
  UserProfile,
  PublicSocialLink,
  PublicWallet,
  LinkedNfcItem,
} from '@/domain/ports/IPublicTrophyRoomGateway';

/**
 * Public Trophy Room state structure
 */
export interface PublicTrophyRoomState {
  // Data
  user: UserProfile | null;
  socialLinks: PublicSocialLink[];
  wallets: PublicWallet[];
  items: LinkedNfcItem[];

  // Loading & Error states
  loading: boolean;
  error: string | null;

  // Current user being viewed
  currentUserId: string | null;
}

const initialState: PublicTrophyRoomState = {
  user: null,
  socialLinks: [],
  wallets: [],
  items: [],
  loading: false,
  error: null,
  currentUserId: null,
};

/**
 * Async Thunk: Fetch public Trophy Room for a specific user
 */
export const fetchPublicTrophyRoomAsync = createAsyncThunk<
  PublicTrophyRoomData,
  string, // userId parameter
  { extra: StoreExtraDependencies; rejectValue: string }
>('publicTrophyRoom/fetch', async (userId, { extra, rejectWithValue }) => {
  try {
    const { publicTrophyRoomGateway } = extra as StoreExtraDependencies & {
      publicTrophyRoomGateway: import('@/domain/ports/IPublicTrophyRoomGateway').IPublicTrophyRoomGateway;
    };

    return await publicTrophyRoomGateway.getPublicTrophyRoom(userId);
  } catch (error) {
    console.error('[publicTrophyRoomSlice] Fetch failed:', error);

    // Handle specific error types
    if (error instanceof Error) {
      if (error.message === 'UserNotFound') {
        return rejectWithValue('User not found. Please check the link and try again.');
      }
      if (error.message === 'NetworkError') {
        return rejectWithValue('Network error. Please check your connection and try again.');
      }
      return rejectWithValue(error.message);
    }

    return rejectWithValue('Unknown error occurred');
  }
});

/**
 * Public Trophy Room Slice
 */
const publicTrophyRoomSlice = createSlice({
  name: 'publicTrophyRoom',
  initialState,
  reducers: {
    /**
     * Clear Trophy Room data (e.g., when navigating away)
     */
    clearTrophyRoom() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder
      // Fetch Public Trophy Room
      .addCase(fetchPublicTrophyRoomAsync.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentUserId = action.meta.arg; // Store userId being fetched

        // Clear previous data when fetching new user
        state.user = null;
        state.socialLinks = [];
        state.wallets = [];
        state.items = [];
      })
      .addCase(fetchPublicTrophyRoomAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload.user;
        state.socialLinks = action.payload.socialLinks;
        state.wallets = action.payload.wallets;
        state.items = action.payload.items;
      })
      .addCase(fetchPublicTrophyRoomAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load Trophy Room';
      });
  },
});

// Actions
export const { clearTrophyRoom } = publicTrophyRoomSlice.actions;

// Selectors
export const selectPublicTrophyRoom = (state: RootState) => state.publicTrophyRoom;

export const selectPublicTrophyRoomLoading = (state: RootState) =>
  state.publicTrophyRoom.loading;

export const selectPublicTrophyRoomError = (state: RootState) =>
  state.publicTrophyRoom.error;

export const selectPublicTrophyRoomUser = (state: RootState) =>
  state.publicTrophyRoom.user;

export const selectPublicTrophyRoomItems = (state: RootState) =>
  state.publicTrophyRoom.items;

export const selectPublicTrophyRoomSocialLinks = (state: RootState) =>
  state.publicTrophyRoom.socialLinks;

export const selectPublicTrophyRoomWallets = (state: RootState) =>
  state.publicTrophyRoom.wallets;

// Reducer
export default publicTrophyRoomSlice.reducer;
